import {
  multiplyObjects,
  multiplyProperties,
  sum,
  sumObjects,
} from '../math.helpers';
import { getEntries, getId, mapObject, omit } from '../object.helpers';
import { ActivityMix } from '../../models/activities.model';
import type { ActivityMix as DesignActivityMix } from '@shared/models/design/activities/activity-type.model';
import { EnergyClass } from './energy.models';
import { isSingleFamilyHouse } from './energy.helpers';
import {
  Baseline,
  IProductElement,
  OneOfElementListElements,
} from '../../models/project.model';
import { IProposal } from '../../models/proposals.model';
import { getProposal } from '../proposals/proposals.helpers';
import { getResolvedBuildingVersion } from '../actions/resolved-version.helpers';
import { flattenElements } from '../recursive-element.helpers';
import { isOneOf } from '../array.helpers';
import {
  getElementCategoryId,
  isElementOfCategory,
} from '../element-category.helpers';
import { getConversionFactorValue } from '../conversion-factors.helpers';
import { getGenericProductId } from '../product-element.helpers';
import {
  defaultEnergyMix,
  EnergyCarrier,
  energyCarrierProductIds,
  EnergyConsumerElementCategory,
  EnergyConsumptionRecord,
  EnergyCarrierMix,
  IEnergyConsumerElement,
  energyCarrierWeightFactors,
  EnergyConsumer,
  defaultEnergyConsumptionRecord,
  EnergyMixUnit,
  EnergyByCarrierByConsumer,
} from './energy-class.model';
import {
  getAllProductElements,
  getChildElements,
} from '../recursive-element.helpers';
import { required } from '../function.helpers';
import { ElementCategoryID } from '../../models/element-categories.model';
import { getElementPropertyResolvedCountByNameOrId } from '../element-property.helpers';
import { cloneDeep } from 'lodash';
import { ItemOrItemId } from '../../models/type-helpers.model';
import { ResultsRecord } from '../../models/unit.model';
import { ENERGY_CLASS_COLORS, ENERGY_CLASSES } from './energy.models';
import { AirHandlingUnitPropertyName } from '@shared/templates/categories/air-handling-unit/air-handling-unit.model';
import { HeatingPropertyName } from '@shared/templates/categories/heating/heating.model';
import { ActivityType } from '@shared/models/design/activities/activity-type.model';

/**
 * Calculate the primary energy value (in kWh/m2), basically the energy performance of the building.
 * In Swedish it is called Primärenergital (EPₚₑₜ)
 * Read more here: https://www.boverket.se/globalassets/publikationer/dokument/2020/konsoliderad-bbr-2011-6-tom-2020-4.pdf
 * Intro: https://www.boverket.se/sv/byggande/bygg-och-renovera-energieffektivt/energihushallningskrav/primarenergital-och-byggnadens-energiprestanda/
 * @param energyConsumption - The energy consumption divided into each energy consumer type
 * @param Atemp - The heated area
 * @param geoFactor - The geo factor (aimed to make comparison between different parts of the country fair)
 * @returns The primary energy value (in kWh/m2)
 */
export const getPrimaryEnergyValues = (
  energyConsumption: EnergyConsumptionRecord,
  Atemp: number,
  geoFactor: number = 1,
): Record<EnergyConsumer, number> => {
  // räkna fram faktorer för varje energiändamål
  const energySums = sumEnergyConsumption(energyConsumption, true); // Only include the consumers that are specified

  // Adjust heating to geoFactor.
  const heating = energySums.heating / geoFactor / Atemp;
  const cooling = energySums.cooling / Atemp;
  const tapwater = energySums.tapwater / Atemp;
  const building = energySums.building / Atemp;

  return {
    heating,
    cooling,
    tapwater,
    building,
  };
};

const sumEnergyConsumption = (
  energyConsumption: EnergyConsumptionRecord,
  applyWeightFactors: boolean = true,
): Record<EnergyConsumer, number> =>
  mapObject(energyConsumption, (value) =>
    sum(applyWeightFactors ? getWeightedEnergyMix(value) : value),
  );

/**
 * Get the energy class of the building from A (best) to G (worst)
 * Read more: https://www.boverket.se/sv/energideklaration/energideklaration/energideklarationens-innehall/
 * A = EP är ≤ 50 procent av kravet för en ny byggnad.
 * B = EP är > 50 - ≤ 75 procent av kravet för en ny byggnad.
 * C = EP är > 75 - ≤ 100 procent kravet för en ny byggnad.
 * D = EP är > 100 - ≤ 135 procent av kravet för en ny byggnad.
 * E = EP är > 135 - ≤ 180 procent av kravet för en ny byggnad.
 * F = EP är > 180 - ≤ 235 procent av kravet för en ny byggnad.
 * G = EP är > 235 procent av kravet för en ny byggnad.
 * From 1 january 2019, the energy performance is also expressed in primary energy value (EPₚₑₜ) instead of specific energy consumption.
 * @param primaryEnergyValue Energy consumtion per GFA
 * @param max Max allowed energy consumption per GFA
 * @returns An energy class
 */
export const getEnergyClass = (
  primaryEnergyValue: number,
  maxAllowedPrimaryEnergyValue: number,
): EnergyClass => {
  // How well is the building performing compared to the max allowed energy consumption? Lower is better.
  const ratio = maxAllowedPrimaryEnergyValue // Houses under 50 m2 are not required to have an energy class
    ? primaryEnergyValue / maxAllowedPrimaryEnergyValue
    : 0;

  if (ratio <= 0.5) return 'A';
  if (ratio <= 0.75) return 'B';
  if (ratio <= 1) return 'C';
  if (ratio <= 1.35) return 'D';
  if (ratio <= 1.8) return 'E';
  if (ratio <= 2.35) return 'F';
  return 'G';
};

/**
 * Get the max allowed primary energy value (kWh/m2) based on the activity mix and the heated area.
 * Page 16: https://rinfo.boverket.se/BFS2011-6/pdf/BFS2024-14.pdf
 * https://www.boverket.se/sv/energideklaration/energideklaration/energideklarationens-innehall/
 * @param heatedArea - The heated area
 * @param activityMix - The activity mix
 * @returns The max allowed primary energy value
 */
export const getMaxAllowedPrimaryEnergyValue = (
  heatedArea: number,
  activityMix?: ActivityMix | DesignActivityMix,
): number => {
  if (heatedArea <= 50) {
    return 0;
  } else if (isSingleFamilyHouse(heatedArea)) {
    if (heatedArea > 130) {
      return 90;
    } else if (heatedArea > 90) {
      return 95;
    } else {
      return 100;
    }
  } else {
    const other = sum(omit(activityMix, ActivityType.Residential));
    const apartment =
      activityMix?.[ActivityType.Residential] ?? heatedArea - other;
    return (other * 70 + apartment * 75) / (other + apartment);
  }
};

/**
 * Get the energy consumer of an element
 * @param element - The element
 * @param productElement - Needed for AHU since that both have heating and building energy consumption
 * @returns The energy consumer
 */
const getEnergyConsumer = (
  element: OneOfElementListElements,
  productElement: IProductElement,
): EnergyConsumer => {
  if (isEnergyConsumerElement(element)) {
    const categoryId = getElementCategoryId(element);
    if (categoryId === ElementCategoryID.Heating) {
      return EnergyConsumer.Heating;
    }

    if (categoryId === ElementCategoryID.Cooling) {
      return EnergyConsumer.Cooling;
    }

    // Air handling unit is a special case since it has both heating and building energy consumption
    if (categoryId === ElementCategoryID.AirHandlingUnit) {
      const children = getChildElements(element);
      const postHeatingProduct =
        getElementPropertyResolvedCountByNameOrId(
          element,
          AirHandlingUnitPropertyName.SupplyAirPostHeating,
        ) && children.indexOf(productElement) === 2 // Check that this is the post heating product
          ? children[children.length - 1]
          : undefined;

      return postHeatingProduct
        ? EnergyConsumer.Heating
        : EnergyConsumer.Building;
    }
  }
  return EnergyConsumer.Building;
};

/**
 * Check if element belongs to a category that have an energy consumption
 * @param element
 * @returns
 */
const isEnergyConsumerElement = (
  element: OneOfElementListElements,
): element is IEnergyConsumerElement =>
  isElementOfCategory(element, ...EnergyConsumerElementCategory);

/**
 * Test the element has energy consumption in B6 (driftfasen) using proposal-scoped results.
 * @param resultsRecord - Proposal-level results keyed by element id
 */
const hasB6EnergyConsumption = (
  element: OneOfElementListElements,
  resultsRecord?: ResultsRecord,
): boolean => {
  const results = resultsRecord?.[element.id];
  if (!results) return false;
  return getConversionFactorValue(results, 'co2e_B6', 'sek_B6') > 0;
};

/**
 * Get all active (in proposal) elements in building version that have energy consumption.
 * Elements are derived from the baseline with the proposal's actions applied
 * (see {@link getResolvedBuildingVersion}).
 *
 * @param buildingVersion - The building version
 * @param proposal - Optional proposal to filter elements by
 * @param resultsRecord - Proposal-level results for B6 filtering
 * @returns
 */
const getActiveElementsWithB6EnergyConsumption = (
  buildingVersion: Baseline,
  proposal?: ItemOrItemId<IProposal | IProposal>,
  resultsRecord?: ResultsRecord,
): IEnergyConsumerElement[] => {
  const proposalId = proposal ? getId(proposal) : undefined;
  const elements = flattenElements(
    getResolvedBuildingVersion(buildingVersion, proposalId),
  );

  return elements
    .filter(isEnergyConsumerElement)
    .filter((el) => hasB6EnergyConsumption(el, resultsRecord));
};

export const getEnergyByCarrierByConsumer = (
  energyConsumptionRecord: EnergyConsumptionRecord,
): EnergyByCarrierByConsumer => {
  const energyByCarrier = getEntries(energyConsumptionRecord).reduce(
    (acc, [consumer, carriers]) => {
      return getEntries(carriers).reduce((carrierAcc, [carrier, value]) => {
        if (value > 0) {
          const consumerAcc =
            acc[carrier] ??
            (acc[carrier] = {} as Record<EnergyConsumer, number>);
          consumerAcc[consumer] = value;
        }
        return carrierAcc;
      }, acc);
    },
    {} as EnergyByCarrierByConsumer,
  );
  return energyByCarrier;
};

/**
 * Get the energy mix for a building version.
 * Reads per-element B6 values from the resolved proposal's {@link ResultsRecord} when
 * present; otherwise falls back to {@link Baseline.resultsRecord} (baseline /
 * synthetic baseline proposal ids that are not stored in {@link Baseline.proposals}).
 * @param buildingVersion - The building version
 * @param buildingLifetime - Provide lifetime to get yearly energy consumption (else a total energy consumption is returned)
 * @param unit - The unit to get the mix in. Defaults to kWh but can be used to get cost or co2e values
 * @param proposal - Optional proposal (or id) to scope elements and read results from
 * @returns The energy mix with numbers in kWh
 */
export const getEnergyConsumptionRecordFromBuildingVersion = (
  buildingVersion?: Baseline,
  buildingLifetime: number = 1,
  unit: EnergyMixUnit = 'kWh',
  proposal?: ItemOrItemId<IProposal | IProposal>,
): EnergyConsumptionRecord => {
  const lifetime = unit === 'kWh' ? buildingLifetime : 1; // Only kWh unit can be yearly, not co2 or sek
  const record = cloneDeep(defaultEnergyConsumptionRecord);

  if (!buildingVersion) {
    return record;
  }

  const proposalId = proposal ? getId(proposal) : undefined;
  const resolvedProposal = getProposal(buildingVersion, proposalId);
  // Fall back to the version's baseline record when no proposal is selected
  // (baseline mode) or when the synthetic baseline proposal id (== version.id)
  // doesn't resolve to a real proposal row. Without this, `hasB6EnergyConsumption`
  // filters every element out and the baseline page reports zero energy.
  const resultsRecord =
    resolvedProposal?.resultsRecord ?? buildingVersion.resultsRecord;

  const energyElements = getActiveElementsWithB6EnergyConsumption(
    buildingVersion,
    proposal,
    resultsRecord,
  );

  return energyElements.reduce((record, element) => {
    return getAllProductElements(element).reduce((record, productElement) => {
      const consumer = getEnergyConsumer(element, productElement);
      const current = record[consumer];
      const energyMix = getEnergyMixFromElement(
        productElement,
        unit,
        resultsRecord,
      );
      const yearlyEnergyMix = multiplyProperties(energyMix, 1 / lifetime);

      const tapwaterShare =
        element.category_id === ElementCategoryID.Heating &&
        getElementPropertyResolvedCountByNameOrId(
          element,
          HeatingPropertyName.TapwaterShare,
          0,
        );

      // Move some of the heating energy mix to tapwater
      if (tapwaterShare) {
        const tapwaterEnergyMix = multiplyProperties(
          yearlyEnergyMix,
          tapwaterShare,
        );
        const spaceHeatingEnergyMix = multiplyProperties(
          yearlyEnergyMix,
          1 - tapwaterShare,
        );

        return {
          ...record,
          [EnergyConsumer.Tapwater]: sumObjects([
            record[EnergyConsumer.Tapwater],
            tapwaterEnergyMix,
          ]),
          [EnergyConsumer.Heating]: sumObjects([
            record[EnergyConsumer.Heating],
            spaceHeatingEnergyMix,
          ]),
        };
      }

      return {
        ...record,
        [consumer]: sumObjects([current, yearlyEnergyMix]),
      };
    }, record);
  }, record);
};

const getWeightedEnergyMix = (energyMix: EnergyCarrierMix): EnergyCarrierMix =>
  multiplyObjects(energyMix, energyCarrierWeightFactors);

/**
 * Get the energy consumption from an element.
 * Reads from resultsRecord when available.
 * @param element - The element
 * @param unit - The unit to get the mix in. Defaults to kWh but can be used to get cost or co2e values
 * @param resultsRecord - Proposal-level results keyed by element id
 * @returns The energy consumption
 */
const getEnergyMixFromElement = (
  element: IEnergyConsumerElement | IProductElement,
  unit: EnergyMixUnit = 'kWh',
  resultsRecord?: ResultsRecord,
): EnergyCarrierMix => {
  const productElements = getAllProductElements(element).filter((el) =>
    hasB6EnergyConsumption(el, resultsRecord),
  );
  return sumObjects(
    productElements.map((element) =>
      getEnergyMixFromProductElement(element, unit, resultsRecord),
    ),
  );
};

/**
 * Get the energy mix from a product element.
 * Reads from resultsRecord when available.
 * @param element - The product element
 * @param unit - Defaults to kWh but can be "co2e_B6" or "sek_B6"
 * @param resultsRecord - Proposal-level results keyed by element id
 * @returns The energy mix with numbers in the provided unit (kWh default)
 */
const getEnergyMixFromProductElement = (
  element: IProductElement,
  unit: EnergyMixUnit = 'kWh',
  resultsRecord?: ResultsRecord,
): EnergyCarrierMix => {
  const carrier = required(
    getEnergyCarrierFromProductElement(element),
    'No energy carrier found for product element',
  );
  const results = resultsRecord?.[element.id];

  if (!results) return defaultEnergyMix;

  return {
    ...defaultEnergyMix,
    [carrier]: getConversionFactorValue(results, unit),
  };
};

export const getEnergyCarrierFromProductElement = (
  element: IProductElement,
): EnergyCarrier | undefined => {
  const id = getGenericProductId(element);
  for (const [carrier, productIds] of getEntries(energyCarrierProductIds)) {
    if (isOneOf(productIds, id)) {
      return carrier;
    }
  }
};

/**
 * Represents energy class information including the class letter, numeric value, and display color.
 * Energy classes range from A (best) to G (worst) according to Swedish building regulations.
 *
 * @see https://www.boverket.se/sv/energideklaration/energideklaration/energideklarationens-innehall/
 */
export class EnergyClassInfo {
  /** The energy class letter (A-G) */
  public readonly energyClass: EnergyClass;

  /** The numeric representation of the energy class (1-7, where 1=A, 7=G) */
  public readonly energyNumber: number;

  /** The hex color code for displaying this energy class.  */
  public readonly color: `#${string}`;

  /**
   * Private constructor - use static factory methods instead
   * @param energyClass - The energy class letter
   */
  private constructor(energyClass: EnergyClass) {
    this.energyClass = energyClass;
    this.energyNumber = ENERGY_CLASSES.indexOf(energyClass) + 1;
    this.color = ENERGY_CLASS_COLORS[energyClass];
  }

  /**
   * Creates an EnergyClassInfo instance from an energy class letter
   * @param energyClass - The energy class letter (A-G)
   * @returns EnergyClassInfo instance
   */
  static fromClass(energyClass: EnergyClass): EnergyClassInfo {
    return new EnergyClassInfo(energyClass);
  }

  /**
   * Creates an EnergyClassInfo instance by calculating the energy class from primary energy values
   * @param primaryEnergyValue - The building's primary energy consumption (kWh/m²)
   * @param regulationTarget - The maximum allowed primary energy consumption (kWh/m²)
   * @returns EnergyClassInfo instance
   */
  static fromPrimaryEnergyValue(
    primaryEnergyValue: number,
    regulationTarget: number,
  ): EnergyClassInfo {
    return new EnergyClassInfo(
      getEnergyClass(primaryEnergyValue, regulationTarget),
    );
  }
}
